/**
 * Restaurant Insights Tab
 * 
 * Zeigt Analytics-Daten für Restaurant Owner:
 * - KPIs: Unique Views, Total Views, Avg Views/Session, Repeat Rate, CTR
 * - Chart: Daily Line/Bar (views + clicks)
 * - Top Referrer Liste
 */

import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Restaurant } from '@/lib/types';
import { TrendingUp, Users, Eye, Repeat, MousePointerClick, ExternalLink, Info, RefreshCw, Download, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface InsightsData {
  views_total: number;
  views_unique_sessions: number;
  views_repeat_sessions: number;
  engagement_ratio: number;
  avg_views_per_session: number;
  repeat_sessions_rate: number;
  clicks_total_by_type: {
    call: number;
    route: number;
    menu: number;
    website: number;
  };
  ctr_by_type: {
    call: number;
    route: number;
    menu: number;
    website: number;
  };
  timeseries_daily: Array<{
    date: string;
    views_unique: number;
    views_total: number;
    call_to_view_ratio: number;
    clicks_call: number;
    clicks_route: number;
    clicks_menu: number;
    clicks_website: number;
  }>;
  top_referrers: Array<{
    domain: string;
    count: number;
  }>;
}

interface RestaurantInsightsTabProps {
  restaurant: Restaurant;
}

/**
 * InfoPopover: Mobile-freundliche Info-Icon mit Popover (Tap-to-open)
 * Desktop: Hover oder Click
 */
function InfoPopover({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-sm hover:bg-gray-100 active:bg-gray-200 p-0.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          aria-label={label}
          title={label}
        >
          <Info className="h-3.5 w-3.5 text-gray-500 hover:text-gray-700" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 text-sm z-50 bg-white border border-gray-200 shadow-lg">
        <div className="space-y-2">
          <div className="font-medium text-gray-900">{label}</div>
          <div className="text-xs text-gray-600 leading-relaxed">{children}</div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const RestaurantInsightsTab: React.FC<RestaurantInsightsTabProps> = ({ restaurant }) => {
  const [liveMode, setLiveMode] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30); // Default: 30 Tage
    
    return {
      from: from.toISOString().split('T')[0],
      to: to.toISOString().split('T')[0],
    };
  });

  const { data: insights, isLoading, error, refetch } = useQuery<InsightsData>({
    queryKey: [`/api/restaurants/${restaurant.id}/insights`, dateRange.from, dateRange.to],
    queryFn: async () => {
      const response = await fetch(
        `/api/restaurants/${restaurant.id}/insights?from=${dateRange.from}&to=${dateRange.to}`,
        {
          credentials: 'include',
        }
      );
      
      if (response.status === 401) {
        // Session abgelaufen - zur Login-Seite umleiten
        window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
        throw new Error('Session abgelaufen. Bitte erneut anmelden.');
      }
      
      if (!response.ok) {
        throw new Error('Fehler beim Laden der Insights');
      }
      
      setLastUpdate(new Date());
      return response.json();
    },
    enabled: !!restaurant.id,
    staleTime: liveMode ? 0 : 2 * 60 * 1000, // Live: 0, sonst 2 Min
    refetchInterval: liveMode ? 10000 : false, // Live: alle 10s, sonst aus
    refetchOnWindowFocus: liveMode ? 'always' : true,
  });

  // WICHTIG: Hooks dürfen nicht nach early-returns stehen (sonst React #310 / Hook order mismatch).
  // Daher chartData IMMER berechnen – bei Loading/Error einfach mit leerem Fallback.
  const timeseries = insights?.timeseries_daily ?? [];

  // Chart-Daten vorbereiten (stabile Keys für Recharts)
  const chartData = useMemo(() => {
    return timeseries
      .slice()
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((d) => ({
        dateIso: d.date,
        dateLabel: new Date(d.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
        uniqueViews: d.views_unique,
        totalViews: d.views_total,
        clicksCall: d.clicks_call,
        clicksRoute: d.clicks_route,
        clicksMenu: d.clicks_menu,
        clicksWebsite: d.clicks_website,
      }));
  }, [timeseries]);

  // Berechne "Aktive Tage" (Tage mit mindestens einem Event)
  const activeDays = useMemo(() => {
    return chartData.filter(d => 
      d.uniqueViews > 0 || d.totalViews > 0 || 
      d.clicksCall > 0 || d.clicksRoute > 0 || 
      d.clicksMenu > 0 || d.clicksWebsite > 0
    ).length;
  }, [chartData]);

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !insights) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-gray-500">
              {error ? 'Fehler beim Laden der Insights' : 'Keine Daten verfügbar'}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Owner-freundliche Kurz-Erklärung (Collapsible) */}
      <Collapsible defaultOpen={false}>
        <Card className="border-blue-200/50 bg-blue-50/40 overflow-hidden">
          <CardHeader className="pb-3 p-0">
            <CollapsibleTrigger asChild>
              <button className="flex items-center justify-between w-full text-left hover:bg-blue-100/50 px-6 py-6 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset">
                <CardTitle className="text-base flex items-center gap-2">
                  <Info className="h-4 w-4 text-blue-600" />
                  <span className="text-gray-900">Was bedeuten diese Zahlen?</span>
                  <span className="text-xs font-normal text-gray-500 ml-1">Kurz erklärt</span>
                </CardTitle>
                <ChevronDown className="h-4 w-4 text-gray-500 transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
              </button>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="text-sm text-gray-700 px-6 pb-6 pt-0 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <div className="font-semibold text-gray-900">📊 Unique Aufrufe</div>
                  <div className="text-xs text-gray-600">Anzahl unterschiedlicher Besucher/Sessions. <strong>Beispiel:</strong> 50 Unique Aufrufe = 50 verschiedene Personen haben dein Profil gesehen.</div>
                </div>
                <div className="space-y-1">
                  <div className="font-semibold text-gray-900">🔢 Gesamtaufrufe</div>
                  <div className="text-xs text-gray-600">Alle Profilaufrufe inkl. Wiederholungen. <strong>Beispiel:</strong> 80 Gesamt = manche kamen mehrfach zurück.</div>
                </div>
                <div className="space-y-1">
                  <div className="font-semibold text-gray-900">🔄 Ø Aufrufe/Session</div>
                  <div className="text-xs text-gray-600">Wie oft ein Besucher durchschnittlich dein Profil ansieht. <strong>Beispiel:</strong> 1.6 = leichtes Interesse.</div>
                </div>
                <div className="space-y-1">
                  <div className="font-semibold text-gray-900">👆 Klicks/Session</div>
                  <div className="text-xs text-gray-600">Aktionen pro Besucher (Anruf/Route/Speisekarte). <strong>Beispiel:</strong> 2.5 = sehr aktiv!</div>
                </div>
              </div>
              <div className="text-xs text-gray-600 bg-white/60 rounded p-3 border border-gray-200">
                <strong className="text-gray-900">💡 Gut zu wissen:</strong>
                <ul className="mt-1 space-y-0.5 list-disc list-inside">
                  <li>Eine "Session" = 1 Browser/Device (Chrome + Edge = 2 Sessions)</li>
                  <li>Adblocker können Sessions verdoppeln (einmal blockiert, einmal durchgelassen)</li>
                  <li>Daten werden alle 10 Sekunden im Live-Modus aktualisiert</li>
                </ul>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
      {/* Date Range Selector + Export + Presets + Live Toggle */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-wrap gap-4 items-center">
            <label className="text-sm font-medium text-gray-700">Zeitraum:</label>
            <div className="flex gap-2">
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
              />
              <span className="self-center text-gray-500">bis</span>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
              />
            </div>
            {/* Presets */}
            <div className="flex gap-1">
              {[7, 30, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => {
                    const to = new Date();
                    const from = new Date();
                    from.setDate(from.getDate() - days);
                    setDateRange({
                      from: from.toISOString().split('T')[0],
                      to: to.toISOString().split('T')[0],
                    });
                  }}
                  className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded text-gray-700"
                >
                  {days}T
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            {/* Refresh Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
            {/* Export Button */}
            <Button
              variant="default"
              size="sm"
              onClick={async () => {
                const url = `/api/restaurants/${restaurant.id}/insights/export?from=${dateRange.from}&to=${dateRange.to}&format=csv`;
                
                try {
                  const response = await fetch(url, { credentials: 'include' });
                  
                  if (response.status === 401) {
                    // Session abgelaufen
                    window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
                    return;
                  }
                  
                  if (!response.ok) {
                    throw new Error('Export fehlgeschlagen');
                  }
                  
                  // Download als CSV
                  const blob = await response.blob();
                  const downloadUrl = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = downloadUrl;
                  a.download = `insights-${restaurant.name}-${dateRange.from}-${dateRange.to}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(downloadUrl);
                } catch (error) {
                  console.error('Export Fehler:', error);
                }
              }}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              CSV
            </Button>
          </div>
        </div>
        {/* Live Mode + Last Update */}
        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <Switch
              id="live-mode"
              checked={liveMode}
              onCheckedChange={setLiveMode}
            />
            <Label htmlFor="live-mode" className="cursor-pointer">
              Live-Modus (alle 10s aktualisieren)
            </Label>
          </div>
          {lastUpdate && (
            <span>
              Zuletzt aktualisiert: {lastUpdate.toLocaleTimeString('de-DE')}
            </span>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              Unique Aufrufe
              <InfoPopover label="Unique Aufrufe erklärt">
                Anzahl unterschiedlicher Sessions, die dein Profil im Zeitraum gesehen haben. Ein Browser/Device erzeugt typischerweise eine eigene Session.
              </InfoPopover>
            </CardTitle>
            <Users className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{insights.views_unique_sessions.toLocaleString('de-DE')}</div>
            <p className="text-xs text-gray-500 mt-1">Unterschiedliche Besuche (Sessions)</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              Gesamtaufrufe
              <InfoPopover label="Gesamtaufrufe erklärt">
                Alle Profilaufrufe im Zeitraum. Wenn jemand dein Profil mehrfach öffnet, zählt das mehrfach.
              </InfoPopover>
            </CardTitle>
            <Eye className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{insights.views_total.toLocaleString('de-DE')}</div>
            <p className="text-xs text-gray-500 mt-1">Alle Profilaufrufe (inkl. Mehrfach)</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-1">
              Ø Aufrufe/Session
              <InfoPopover label="Ø Aufrufe/Session erklärt">
                Durchschnittliche Profilaufrufe pro Session. Ein Profilaufruf wird innerhalb einer Session maximal 1× je 10 Minuten gezählt (Anti-Spam).
              </InfoPopover>
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{insights.avg_views_per_session.toFixed(1)}</div>
            <p className="text-xs text-gray-500 mt-1">Interesse/Engagement</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              Wiederkehrende Sessions
              <InfoPopover label="Wiederkehrende Sessions erklärt">
                Anteil der Sessions, die dein Profil mindestens 2× gesehen haben (zeigt Wiederkehr/Interesse).
              </InfoPopover>
            </CardTitle>
            <Repeat className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(insights.repeat_sessions_rate * 100).toFixed(1)}%</div>
            <p className="text-xs text-gray-500 mt-1">{insights.views_repeat_sessions} Sessions</p>
          </CardContent>
        </Card>
      </div>

      {/* Klicks pro Session Cards - keine Prozente, da mehrere Klicks pro Session möglich */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              Anrufe
              <InfoPopover label="Anrufe erklärt">
                Anzahl der Klicks auf „Anrufen". Der Wert darunter zeigt die durchschnittlichen Klicks pro Session.
              </InfoPopover>
            </CardTitle>
            <MousePointerClick className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{insights.clicks_total_by_type.call}</div>
            <p className="text-xs text-gray-500 mt-1">{insights.ctr_by_type.call.toFixed(2)} Klicks/Session</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              Route-Klicks
              <InfoPopover label="Route-Klicks erklärt">
                Anzahl der Klicks auf „Route planen" (Google Maps).
              </InfoPopover>
            </CardTitle>
            <MousePointerClick className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{insights.clicks_total_by_type.route}</div>
            <p className="text-xs text-gray-500 mt-1">{insights.ctr_by_type.route.toFixed(2)} Klicks/Session</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              Speisekarten-Klicks
              <InfoPopover label="Speisekarten-Klicks erklärt">
                Anzahl der Klicks auf „Speisekarte" (öffnen/anzeigen/download).
              </InfoPopover>
            </CardTitle>
            <MousePointerClick className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{insights.clicks_total_by_type.menu}</div>
            <p className="text-xs text-gray-500 mt-1">{insights.ctr_by_type.menu.toFixed(2)} Klicks/Session</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              Website-Klicks
              <InfoPopover label="Website-Klicks erklärt">
                Anzahl der Klicks auf „Website besuchen".
              </InfoPopover>
            </CardTitle>
            <MousePointerClick className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{insights.clicks_total_by_type.website}</div>
            <p className="text-xs text-gray-500 mt-1">{insights.ctr_by_type.website.toFixed(2)} Klicks/Session</p>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              Tagesverlauf
              <InfoPopover label="Tagesverlauf erklärt">
                Zeigt die Entwicklung über den Zeitraum. Wenn erst an einem Tag Daten vorhanden sind, siehst du nur einen Punkt.
              </InfoPopover>
            </CardTitle>
            {chartData.length > 0 && (
              <div className="text-xs text-gray-500">
                <span className="font-medium">Aktive Tage:</span> {activeDays} von {chartData.length}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <div className="text-center p-6">
                <div className="text-4xl mb-2">📊</div>
                <div className="text-sm font-medium text-gray-900 mb-1">Noch keine Daten im Zeitraum</div>
                <div className="text-xs text-gray-500">
                  Wähle einen anderen Zeitraum oder warte, bis erste Besucher dein Profil sehen.
                </div>
              </div>
            </div>
          ) : chartData.length < 2 ? (
            <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-yellow-800">
                  <strong>Noch wenig Daten:</strong> Für einen aussagekräftigen Verlauf brauchst du mindestens 2 Tage mit Aktivität. Erweitere den Zeitraum auf 7-30 Tage für bessere Insights.
                </div>
              </div>
            </div>
          ) : null}
          {chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="dateLabel" 
                tick={{ fontSize: 12 }}
                angle={chartData.length > 14 ? -45 : 0}
                textAnchor={chartData.length > 14 ? "end" : "middle"}
                height={chartData.length > 14 ? 60 : 30}
                interval="preserveStartEnd"
              />
              <YAxis allowDecimals={false} domain={[0, (dataMax: number) => Math.max(1, dataMax)]} tick={{ fontSize: 12 }} />
              <RechartsTooltip
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px' }}
                labelStyle={{ fontWeight: 'bold', marginBottom: '4px' }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Line name="Unique Aufrufe" type="monotone" dataKey="uniqueViews" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line name="Gesamtaufrufe" type="monotone" dataKey="totalViews" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Clicks Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Klicks nach Typ
            <InfoPopover label="Klicks nach Typ erklärt">
              Klicks auf die wichtigsten Aktionen. Wenn ein Besucher mehrfach klickt, steigt der Wert entsprechend.
            </InfoPopover>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="dateLabel" 
                tick={{ fontSize: 12 }}
                angle={chartData.length > 14 ? -45 : 0}
                textAnchor={chartData.length > 14 ? "end" : "middle"}
                height={chartData.length > 14 ? 60 : 30}
                interval="preserveStartEnd"
              />
              <YAxis allowDecimals={false} domain={[0, (dataMax: number) => Math.max(1, dataMax)]} tick={{ fontSize: 12 }} />
              <RechartsTooltip
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px' }}
                labelStyle={{ fontWeight: 'bold', marginBottom: '4px' }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar name="Anrufe" dataKey="clicksCall" fill="#ef4444" />
              <Bar name="Route" dataKey="clicksRoute" fill="#3b82f6" />
              <Bar name="Speisekarte" dataKey="clicksMenu" fill="#10b981" />
              <Bar name="Website" dataKey="clicksWebsite" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
          )}
          {chartData.length === 0 && (
            <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <div className="text-center p-6">
                <div className="text-4xl mb-2">📈</div>
                <div className="text-sm font-medium text-gray-900 mb-1">Noch keine Klicks im Zeitraum</div>
                <div className="text-xs text-gray-500">
                  Sobald Besucher auf Anrufen, Route, Speisekarte oder Website klicken, siehst du hier die Verteilung.
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Referrers */}
      {insights.top_referrers && insights.top_referrers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Top Referrer</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {insights.top_referrers.map((ref, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-2">
                    <ExternalLink className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-medium">{ref.domain}</span>
                  </div>
                  <span className="text-sm text-gray-600">{ref.count.toLocaleString('de-DE')}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RestaurantInsightsTab;
